import {Button, Card, Flags, FormControl, FormField} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';

import {useWatch} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {useGetUsersQuery} from '@dms/api/customer';
import {TelephonyGroup} from '@dms/api/settings';
import {UserStateFilterSchema} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {getUserName} from '@dms/shared';

import {composePath, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {
  getRingStrategyLabel,
  getTelephonyGroupSummary,
} from '../../TelephonyGroups/utils/getTelephonyGroupSummary';
import {TelephonyLineRoutingFormValues} from '../utils/telephonyLineRoutingFormSchema';

interface TelephonyLineRoutingOpenTargetCardProps extends RequiredTestIdProps {
  control: FormControl<TelephonyLineRoutingFormValues>;
  groups: TelephonyGroup[];
  areGroupsLoading: boolean;
  isReadOnly: boolean;
}

export function TelephonyLineRoutingOpenTargetCard(props: TelephonyLineRoutingOpenTargetCardProps) {
  const navigate = useNavigate();

  const openTargetKind = useWatch({control: props.control, name: 'openTargetKind'});
  const groupId = useWatch({control: props.control, name: 'groupId'});

  const {data: users, isLoading: areUsersLoading} = useGetUsersQuery({
    stateFilter: UserStateFilterSchema.enum.active,
  });

  const selectedGroup = props.groups.find((group) => group.id === groupId);

  const groupOptions = props.groups.map((group) => ({
    value: group.id,
    label: `${group.name} · ${getTelephonyGroupSummary(group)}`,
  }));

  // A person without a mobile number would ring nobody, so the picker refuses them up front.
  const userOptions =
    users
      ?.filter((user) => !user.blocked && isNotNil(user.phoneNumber))
      .map((user) => ({value: user.id, label: getUserName(user) ?? user.email})) ?? [];

  const handleEditGroup = () => {
    if (isNil(selectedGroup)) {
      return;
    }

    navigate(
      composePath(settingsRoutes.telephonyGroupDetail, {params: {groupId: selectedGroup.id}})
    );
  };

  return (
    <Card
      title={i18n.t('page.settings.telephony.routingOpenCardTitle')}
      subtitle={i18n.t('page.settings.telephony.routingOpenCardSubtitle')}
      data-testid={suffixTestId('card', props)}
    >
      <VStack spacing={4}>
        <FormField
          control={props.control}
          name="openTargetKind"
          type="radio"
          options={[
            {label: i18n.t('page.settings.telephony.routingTargetGroup'), value: 'group'},
            {label: i18n.t('page.settings.telephony.routingTargetUser'), value: 'user'},
          ]}
          isDisabled={props.isReadOnly}
          data-testid={suffixTestId('kind', props)}
        />
        <Text size="small" color="secondary">
          {openTargetKind === 'group'
            ? i18n.t('page.settings.telephony.routingTargetGroupDescription')
            : i18n.t('page.settings.telephony.routingTargetUserDescription')}
        </Text>
        <Show when={openTargetKind === 'group'}>
          <FormField
            control={props.control}
            name="groupId"
            type="choice"
            label={i18n.t('page.settings.telephony.routingGroupLabel')}
            options={groupOptions}
            isLoading={props.areGroupsLoading}
            isRequired
            isDisabled={props.isReadOnly}
            data-testid={suffixTestId('group', props)}
          />
          <Show when={isNotNil(selectedGroup)}>
            <Box
              borderRadius="small"
              border="1px solid"
              borderColor="general.separator"
              padding={3}
              data-testid={suffixTestId('groupSummary', props)}
            >
              <HStack justify="space-between" align="center" spacing={2}>
                <VStack spacing={1}>
                  <HStack spacing={2} align="center">
                    <Text size="small" alternative>
                      {selectedGroup?.name}
                    </Text>
                    <Show when={selectedGroup}>
                      <Flags
                        size="small"
                        flags={[
                          {
                            label: getRingStrategyLabel(
                              selectedGroup?.ringStrategy ?? 'simultaneous'
                            ),
                            colorScheme:
                              selectedGroup?.ringStrategy === 'simultaneous' ? 'blue' : 'neutral',
                            isSubtle: true,
                          },
                        ]}
                      />
                    </Show>
                  </HStack>
                  <Text size="xSmall" color="secondary">
                    {isNotNil(selectedGroup) ? getTelephonyGroupSummary(selectedGroup) : null}
                  </Text>
                </VStack>
                <Button
                  variant="link"
                  size="small"
                  title={i18n.t('page.settings.telephony.editGroup')}
                  onClick={handleEditGroup}
                  data-testid={suffixTestId('editGroup', props)}
                />
              </HStack>
            </Box>
          </Show>
        </Show>
        <Show when={openTargetKind === 'user'}>
          <FormField
            control={props.control}
            name="userId"
            type="choice"
            label={i18n.t('page.settings.telephony.routingUserLabel')}
            options={userOptions}
            isLoading={areUsersLoading}
            helperText={i18n.t('page.settings.telephony.routingUserNoPhoneHint')}
            isRequired
            isDisabled={props.isReadOnly}
            data-testid={suffixTestId('user', props)}
          />
        </Show>
      </VStack>
    </Card>
  );
}
